import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ExternalLink, Filter, Star } from 'lucide-react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useNavigate } from 'react-router-dom';
import { useScroll } from '@/contexts/ScrollContext';

const portfolioItems = [
  {
    id: "report-scribe-ehr",
    title: "AI-Powered Customer Service Platform",
    description: "An intelligent customer service solution that uses natural language processing to automate responses and improve customer satisfaction.",
    image: "/placeholder.svg",
    tags: ["Artificial Intelligence", "NLP", "Customer Service"]
  },
  {
    id: "med-flow-emr",
    title: "Enterprise Resource Planning System",
    description: "A comprehensive ERP solution that streamlines business operations and improves efficiency across departments.",
    image: "/placeholder.svg",
    tags: ["ERP", "Business Intelligence", "Process Automation"]
  },
  {
    id: "resume-ai",
    title: "Healthcare Management Platform",
    description: "A secure platform for healthcare providers to manage patient data, appointments, and treatment plans.",
    image: "/placeholder.svg",
    tags: ["Healthcare", "Data Security", "UX Design"]
  },
  {
    id: "hotel-inventory-ai",
    title: "E-commerce Analytics Dashboard",
    description: "A real-time analytics dashboard that helps e-commerce businesses track performance and identify growth opportunities.",
    image: "/placeholder.svg",
    tags: ["Analytics", "E-commerce", "Data Visualization"]
  },
  {
    id: "warehouse-support",
    title: "Supply Chain Management System",
    description: "An end-to-end solution for tracking inventory, managing suppliers, and optimizing logistics operations.",
    image: "/placeholder.svg",
    tags: ["Supply Chain", "Logistics", "Inventory Management"]
  },
  {
    id: "feedback-ai",
    title: "Customer Sentiment Intelligence Platform",
    description: "A comprehensive learning management system with interactive content and progress tracking for students and educators.",
    image: "/placeholder.svg",
    tags: ["Customer Experience", "Sentiment Analysis", "Business Intelligence"]
  },
  {
    id: "med-insight",
    title: "Educational Technology Platform",
    description: "A comprehensive learning management system with interactive content and progress tracking for students and educators.",
    image: "/placeholder.svg",
    tags: ["Education", "EdTech", "Learning Management"]
  },
  {
    id: "finance-flow",
    title: "Educational Technology Platform",
    description: "A comprehensive learning management system with interactive content and progress tracking for students and educators.",
    image: "/placeholder.svg",
    tags: ["Education", "EdTech", "Learning Management"]
  }
];

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [heroVisible, setHeroVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const [showMoreCards, setShowMoreCards] = useState(false);
  const navigate = useNavigate();

  const processRef = useRef<HTMLElement>(null);
  const { isAutoScrolling } = useScroll();

  useEffect(() => {
    const heroTimer = setTimeout(() => {
      if (!isAutoScrolling) {
        setHeroVisible(true);
      }
    }, 200);

    const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !isAutoScrolling) {
              setProcessVisible(true);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '-10% 0px'
        }
    );

    const observerTimer = setTimeout(() => {
      if (processRef.current) {
        observer.observe(processRef.current);
      }
    }, 400);

    return () => {
      clearTimeout(heroTimer);
      clearTimeout(observerTimer);
      observer.disconnect();
    };
  }, [isAutoScrolling]);

  useEffect(() => {
    if (!isAutoScrolling) {
      const timer = setTimeout(() => {
        setHeroVisible(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAutoScrolling]);

  const allTags = ["All", ...new Set(portfolioItems.flatMap(item => item.tags))];

  const filteredItems = activeFilter === "All"
      ? portfolioItems
      : portfolioItems.filter(item => item.tags.includes(activeFilter));

  return (
      <div className="min-h-screen">
        <Header />

        <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-primary/20 via-secondary/30 to-accent/20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full blur-3xl opacity-30"></div>
          </div>

          {/* Plus symbols pattern overlay */}
          <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `
              url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%233B82F6' fill-opacity='0.6'%3E%3Cpath d='M20 18v-2h-2v2h-2v2h2v2h2v-2h2v-2h-2z'/%3E%3C/g%3E%3C/svg%3E")
            `,
                backgroundSize: '40px 40px'
              }}
          ></div>

          {/* Gradient fade to background at bottom */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background via-background/80 to-transparent" />

          <div className="max-w-7xl mx-auto text-center relative z-10">
            <h1
                className={cn(
                    "text-4xl md:text-5xl font-bold mb-6 text-foreground transition-all duration-700",
                    heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
            >
              Our Portfolio
            </h1>
            <p
                className={cn(
                    "text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
                    heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
            >
              Explore our showcase of successful projects that demonstrate our expertise in creating innovative software solutions.
            </p>
          </div>

        </section>

        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Left-aligned title */}
            <div className="mb-16">
              <div className="relative">
                <h2
                    className={cn(
                        "text-3xl md:text-4xl font-bold mb-3 transition-all duration-700",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                >
                  Featured Projects
                </h2>
                <p
                    className={cn(
                        "text-muted-foreground text-lg mb-4 transition-all duration-700 delay-400",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                >
                  Showcasing our most impactful and innovative solutions
                </p>
                <div
                    className={cn(
                        "w-20 h-1 bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all duration-700 delay-300",
                        heroVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
                    )}
                ></div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Column */}
              <div className="flex flex-col gap-6 flex-1">
                {/* Top-left Card - Taller */}
                <div
                    onClick={() => navigate(`/project/report-scribe-ehr`)}
                    className={cn(
                        "relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] h-[600px]",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                    style={{
                      backgroundImage: "url('/images/report-scribe-ehr/5.png')",
                      backgroundPosition: "55% center",
                      backgroundSize: "cover"
                    }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                  <div className="relative h-full p-8 flex flex-col justify-between text-white">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 bg-white rounded mr-3"></div>
                        <span className="text-xl font-semibold">ReportScribe EHR</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-4">Automated Medical Report Generator</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Healthcare</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Document Automation</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">PDF Processing</span>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                          <ExternalLink size={20} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom-left Card - Shorter */}
                <div
                    onClick={() => navigate('/project/med-flow-emr')}
                    className={cn(
                        "relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] delay-400 h-[450px]",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                    style={{
                      backgroundImage: "url('/images/med-flow-emr/1.png')",
                      backgroundPosition: "50% 30%",
                      backgroundSize: "120%"
                    }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                  <div className="relative h-full p-6 flex flex-col justify-between text-white">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 bg-green-400 rounded mr-2"></div>
                        <span className="text-lg font-semibold">MedFlow EMR</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold mb-4">Smart Meeting & Appointment Management System</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Healthcare</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Speech-to-Text</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Scheduling</span>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <ExternalLink size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-6 flex-1">
                {/* Top-right Card - Shorter */}
                <div
                    onClick={() => navigate('/project/resume-ai')}
                    className={cn(
                        "relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] delay-200 h-[450px]",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                    style={{
                      backgroundImage: "url('/images/resume-ai/5.png')",
                      backgroundPosition: "50% 30%",
                      backgroundSize: "120%"
                    }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                  <div className="relative h-full p-6 flex flex-col justify-between text-white">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 bg-white rounded mr-2"></div>
                        <span className="text-lg font-semibold">Resume AI</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold mb-4">Intelligent Resume Classifier & Interview Assistant</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Recruitment Automation</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Ethical AI</span>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <ExternalLink size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom-right Card - Taller */}
                <div
                    onClick={() => navigate('/project/hotel-inventory-ai')}
                    className={cn(
                        "relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] delay-600 h-[600px]",
                        heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    )}
                    style={{
                      backgroundImage: "url('/images/hotel-inventory-ai/5.png')",
                      backgroundPosition: "50% 30%",
                      backgroundSize: "130%"
                    }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                  <div className="relative h-full p-6 flex flex-col justify-between text-white">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 bg-white rounded mr-2"></div>
                        <span className="text-lg font-semibold">Hotel Inventory AI</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold mb-4">Intelligent Inventory Optimization for Hospitality</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Hospitality</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Operational Efficiency</span>
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Cost Optimization</span>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <ExternalLink size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Explore More Button */}
            {!showMoreCards && (
                <div className="text-center mt-12">
                  <button
                      onClick={() => setShowMoreCards(true)}
                      className={cn(
                          "px-8 py-3 rounded-full bg-primary text-white font-medium shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all duration-300",
                          heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                      )}
                  >
                    Explore More
                  </button>
                </div>
            )}

            {/* Additional Cards */}
            {showMoreCards && (
                <div className={cn(
                    "flex flex-col md:flex-row gap-6 mt-12 transition-all duration-700",
                    showMoreCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}>
                  {/* Left Column */}
                  <div className="flex flex-col gap-6 flex-1">
                    {/* FinTech Card - Taller */}
                    <div
                        onClick={() => navigate('/project/warehouse-support')}
                        className="relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] h-[600px]"
                        style={{
                          backgroundImage: "url('/images/warehouse-support/5.png')",
                          backgroundPosition: "50% 30%",
                          backgroundSize: "140%"
                        }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                      <div className="relative h-full p-8 flex flex-col justify-between text-white">
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-white rounded mr-3"></div>
                            <span className="text-xl font-semibold">Warehouse Support</span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-2xl md:text-3xl font-bold mb-4">AI Knowledge & Assistance Platform for Operations</h3>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Employee Support</span>
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">AI Assistance</span>
                          </div>
                          <div className="flex justify-end">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                              <ExternalLink size={20} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* IoT Card - Shorter */}
                    <div
                        onClick={() => navigate('/project/feedback-ai')}
                        className="relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] h-[450px]"
                        style={{
                          backgroundImage: "url('/images/feedback-ai/5.png')",
                          backgroundPosition: "50% 30%",
                          backgroundSize: "130%"
                        }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                      <div className="relative h-full p-6 flex flex-col justify-between text-white">
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="w-6 h-6 bg-white rounded mr-2"></div>
                            <span className="text-lg font-semibold">Feedback AI</span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold mb-4">Customer Sentiment Intelligence Platform</h3>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Customer Experience</span>
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Sentiment Analysis</span>
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Business Intelligence</span>
                          </div>
                          <div className="flex justify-end">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                              <ExternalLink size={16} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="flex flex-col gap-6 flex-1">
                    {/* E-learning Card - Shorter */}
                    <div
                        className="relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] h-[450px]"
                        style={{
                          backgroundImage: "url('/images/med-insight/5.png')",
                          backgroundPosition: "50% 30%",
                          backgroundSize: "130%"
                        }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                      <div onClick={() => navigate('/project/med-insight')} className="relative h-full p-6 flex flex-col justify-between text-white">
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="w-6 h-6 bg-white rounded mr-2"></div>
                            <span className="text-lg font-semibold">Med Insight</span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold mb-4">AI Medical Report Summarization & Insights Platform</h3>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Healthcare Innovation</span>
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">AI Insights</span>
                          </div>
                          <div className="flex justify-end">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                              <ExternalLink size={16} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Social Media Card - Taller */}
                    <div
                        className="relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 hover:scale-[1.02] h-[600px]"
                        style={{
                          backgroundImage: "url('/images/finance-flow/5.png')",
                          backgroundPosition: "50% 30%",
                          backgroundSize: "140%"
                        }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-black/40"></div>
                      <div onClick={() => navigate('/project/finance-flow')} className="relative h-full p-6 flex flex-col justify-between text-white">
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="w-6 h-6 bg-white rounded mr-2"></div>
                            <span className="text-lg font-semibold">Finance Flow</span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold mb-4">AI Expense & Cash Flow Management Platform for SMEs</h3>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Financial Management</span>
                            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Predictive Analytics</span>
                          </div>
                          <div className="flex justify-end">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                              <ExternalLink size={16} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}
          </div>
        </section>

        <section className="py-16 px-6 md:px-10 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 p-4 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground italic text-center">
                All projects shown in this portfolio were built by our team for skill showcase and are not commissioned work. Due to NDAs and client confidentiality, we do not display actual client projects publicly.
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
  );
};

export default PortfolioPage;